import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function PHCalculator() {
  const [concentration, setConcentration] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "pH Calculator", url: "https://calclab.app/calculator/ph" }
    ], lang),
    (lang) => calculatorToolSchema(
      "pH Calculator",
      "Calculate pH from hydrogen ion concentration. Free online chemistry calculator with instant results and detailed explanations.",
      "https://calclab.app/calculator/ph",
      lang
    )
  ]);

  const calculatePH = () => {
    const conc = parseFloat(concentration);

    if (isNaN(conc) || conc <= 0) {
      toast.error("Please enter a valid concentration");
      return;
    }

    const ph = -Math.log10(conc);
    setResult(ph);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setConcentration("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="pH Calculator" 
      calculatorPath="/calculator/ph"
      category="chemistry"
      subcategory="Acids & Bases"
    >
      <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">pH Calculator</h1>
            <p className="text-muted-foreground">
              Calculate pH from H⁺ ion concentration
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: pH = -log₁₀[H⁺]</CardTitle>
              <CardDescription>
                Where [H⁺] is the hydrogen ion concentration in mol/L
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="concentration">H⁺ Concentration (mol/L)</Label>
                  <Input
                    id="concentration"
                    type="number"
                    step="0.0000001"
                    placeholder="Enter concentration"
                    value={concentration}
                    onChange={(e) => setConcentration(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculatePH} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">pH Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-3xl font-bold text-primary break-all overflow-wrap-anywhere">
                            {result.toFixed(2)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {result < 7 ? "Acidic" : result === 7 ? "Neutral" : "Basic"}
                          </p>
                        </div>
                        <CopyResultButton value={result.toFixed(2)} />
                      </div>
                    </CardContent>
                  </Card>

                  <CalculatorSuperCalcCTA config={calculatorCTAs["ph"]} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">pH Scale</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>• pH &lt; 7: Acidic solution</p>
                  <p>• pH = 7: Neutral solution (pure water)</p>
                  <p>• pH &gt; 7: Basic (alkaline) solution</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: [H⁺] = 0.001 mol/L → pH = 3.00 (acidic)
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
